import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { manualsApi } from '@/api/manuals'
import { useEffectiveRole } from '@/store/auth'
import Spinner from '@/components/common/Spinner'
import { BookOpen, ExternalLink } from 'lucide-react'

/**
 * Страница «Помощь» — пользовательские руководства строго по ролям.
 * Директор/зам видят все три руководства (вкладки), ГИП и проектировщик — своё.
 * HTML грузится через API с JWT и рендерится в iframe (srcDoc).
 */
export default function HelpPage() {
  const effectiveRole = useEffectiveRole()

  const { data: manuals = [], isLoading: listLoading } = useQuery({
    queryKey: ['manuals'],
    queryFn: () => manualsApi.list().then((r) => r.data),
  })

  // В режиме «просмотр от имени» показываем руководство просматриваемой роли
  const visibleManuals = useMemo(() => {
    if (effectiveRole === 'chief_engineer') return manuals.filter((m) => m.key === 'gip')
    if (effectiveRole === 'designer') return manuals.filter((m) => m.key === 'designer')
    return manuals
  }, [manuals, effectiveRole])

  const [activeKey, setActiveKey] = useState<string | null>(null)
  useEffect(() => {
    if (visibleManuals.length && (!activeKey || !visibleManuals.some((m) => m.key === activeKey))) {
      setActiveKey(visibleManuals[0].key)
    }
  }, [visibleManuals, activeKey])

  const { data: html, isLoading: htmlLoading } = useQuery({
    queryKey: ['manual-html', activeKey],
    queryFn: () => manualsApi.get(activeKey!).then((r) => r.data),
    enabled: !!activeKey,
    staleTime: Infinity, // содержимое руководства меняется только с деплоем
  })

  function openInNewTab() {
    if (!html) return
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
    window.open(URL.createObjectURL(blob), '_blank')
  }

  return (
    <div className="flex h-full flex-col">
      {/* Шапка */}
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-2.5">
          <BookOpen className="h-5 w-5 text-brand-600" />
          <h1 className="text-lg font-semibold text-gray-900">Помощь</h1>
          {visibleManuals.length > 1 && (
            <nav className="ml-4 flex gap-1">
              {visibleManuals.map((m) => (
                <button
                  key={m.key}
                  onClick={() => setActiveKey(m.key)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeKey === m.key
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                  }`}
                >
                  {m.title}
                </button>
              ))}
            </nav>
          )}
          {visibleManuals.length === 1 && (
            <span className="ml-2 text-sm text-gray-500">{visibleManuals[0].title}</span>
          )}
        </div>
        <button
          onClick={openInNewTab}
          disabled={!html}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <ExternalLink className="h-4 w-4" />
          Открыть в новой вкладке
        </button>
      </div>

      {/* Содержимое руководства */}
      <div className="flex-1 bg-gray-50">
        {(listLoading || htmlLoading) && (
          <div className="flex h-full items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}
        {!listLoading && !visibleManuals.length && (
          <div className="flex h-full items-center justify-center text-sm text-gray-500">
            Для вашей роли руководство пока не добавлено
          </div>
        )}
        {html && !htmlLoading && (
          <iframe
            title="Руководство пользователя"
            srcDoc={html}
            sandbox="allow-same-origin"
            className="h-full w-full border-0 bg-white"
          />
        )}
      </div>
    </div>
  )
}
